import {
  getNewFilterGroupNode,
  toQueryFilter,
} from '@modules/queryBuilder/helpers';
import { upsertMultipleQueryFilterNodes } from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { QueryBuilderAction } from '@modules/queryBuilder/store/types';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { useCallback } from 'react';
import { NormalisedNode } from 'v2/querybuilder/types/querystore';

export interface QuickFilterParams {
  field: string;
  values: string[];
}

interface UseAddQuickFilterOptions {
  customQBStoreFields?: {
    rootID: string;
    nodesByID: Record<string, NormalisedNode>;
    dispatch: (action: QueryBuilderAction) => QueryBuilderAction;
  };
}

export function useAddQuickFilter({
  customQBStoreFields,
}: UseAddQuickFilterOptions = {}) {
  const defaultStoreRootID = useQueryBuilderStore(({ query }) => query.rootID);
  const defaultNodesByID = useQueryBuilderStore(({ query }) => query.nodes);
  const defaultDispatch = useQueryBuilderStore(({ dispatch }) => dispatch);

  const dispatch = customQBStoreFields?.dispatch ?? defaultDispatch;
  const rootID = customQBStoreFields?.rootID ?? defaultStoreRootID;
  const nodesByID = customQBStoreFields?.nodesByID ?? defaultNodesByID;

  return useCallback(
    (filters: QuickFilterParams[]) => {
      const groupNode =
        filters.length > 1
          ? getNewFilterGroupNode({ parentID: rootID, conjunction: 'AND' })
          : undefined;

      const nodes = filters.map((filter) => {
        return toQueryFilter({
          fieldName: filter.field,
          parent: groupNode ? groupNode.id : rootID,
          values: filter.values,
        });
      });

      const updatedNodes = upsertMultipleQueryFilterNodes({
        nodesToUpsert: nodes,
        groupNodeToUpsert: groupNode,
        rootID,
        nodesByID,
      });

      dispatch({
        type: QueryBuilderActionType.SET_QUERY,
        payload: {
          nodes: updatedNodes,
        },
      });

      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          shouldAutoExecuteOnce: true,
        },
      });
    },
    [dispatch, nodesByID, rootID],
  );
}
